/**
 * The bar above every workforce workspace. EP-005.
 *
 *   eMerge · [Patient Advocate] [UMED] [Provider]        Admin console   Jane Doe ▾
 *                                                                        ├ Profile & settings
 *                                                                        ├ Sign-in security
 *                                                                        ├ Land here when I sign in
 *                                                                        └ Sign out
 *
 * Workspaces are links, not a hidden select: a person who holds several sees
 * them all and which one they are in. An administrator always has a way back
 * to the console. Everyone has their profile, their sign-in security and
 * Sign out one click away. What is listed comes from the server's index;
 * each workspace still enforces itself when opened.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useInRouterContext, useNavigate } from 'react-router-dom';
import { useOptionalUser } from '../contexts/UserContext';
import { confirmSignOutWithUnsent } from '../config/authRoutes';
import { useNotificationFeed } from '../contexts/NotificationsContext';
import NotificationBell from './NotificationBell';
import { rememberWorkspace, Workspace, WorkspaceKey, workspacesApi } from '../services/workspacesApi';

type Props = {
  /** The workspace this bar sits on; omit on pages that belong to no workspace (settings). */
  current?: WorkspaceKey;
};

/** Renders nothing outside a signed-in, routed app (for example a shell rendered alone in a test). */
export default function WorkspaceMenu(props: Props) {
  const session = useOptionalUser();
  const routed = useInRouterContext();
  if (!session || !routed || !session.isAuthenticated) return null;
  return <Bar {...props} />;
}

const BAR: React.CSSProperties = { background: '#10252d', color: '#e9f1ef', fontSize: 13 };
const LINK = 'rounded px-2 py-1 text-[13px] leading-5 outline-none focus-visible:ring-2 focus-visible:ring-[#7fd1c3]';

function Bar({ current }: Props) {
  const { user, logout } = useOptionalUser()!;
  const notifications = useNotificationFeed();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [preferred, setPreferred] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const menuId = useId();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    let live = true;
    if (current) rememberWorkspace(current);
    Promise.resolve()
      .then(() => workspacesApi.index())
      .then((r) => {
        if (!live || !r || !r.success || !r.data) return;
        setWorkspaces(r.data.workspaces.filter((w) => w.available));
        setPreferred(r.data.preferred || null);
      })
      .catch(() => undefined);
    return () => { live = false; };
  }, [current]);

  // Close the menu on an outside click or Escape, returning focus to its button.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const work = workspaces.filter((w) => w.key !== 'ADMIN');
  const admin = workspaces.find((w) => w.key === 'ADMIN');
  const canPrefer = Boolean(current) && workspaces.length > 1 && preferred !== current;

  const makeDefault = async () => {
    if (!current) return;
    setNote('');
    const r = await workspacesApi.setPreference(current).catch(() => null);
    if (r && r.success) { setPreferred(current); setNote('Saved — you will land here when you sign in.'); }
    else setNote('Could not save that. Try again.');
    setOpen(false);
  };

  const signOut = async () => {
    setOpen(false);
    // A sign-out deletes this person's unsent notes on this computer: ask first.
    if (!confirmSignOutWithUnsent(user?.id)) return;
    await logout();
    navigate('/login', { replace: true });
  };

  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Account';

  return (
    <nav aria-label="Workspaces" style={BAR} className="w-full">
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-3 gap-y-1 px-4 py-1.5">
        <Link to="/home" className={`${LINK} font-semibold tracking-wide text-white`}>eMerge</Link>

        {work.length > 0 && (
          <ul className="flex flex-wrap items-center gap-1" aria-label="Your workspaces">
            {work.map((w) => {
              const here = w.key === current;
              return (
                <li key={w.key}>
                  <Link
                    to={w.path}
                    aria-current={here ? 'page' : undefined}
                    className={`${LINK} ${here ? 'bg-white/15 font-semibold text-white' : 'text-white/75 hover:bg-white/10 hover:text-white'}`}
                  >
                    {w.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        {note && <span role="status" className="text-white/80">{note}</span>}

        <div className="ml-auto flex items-center gap-2">
          {/* One bell for every workspace: a notification is addressed to a
              person, not to a page, so it belongs on the furniture that
              follows the person. */}
          <NotificationBell state={notifications} />
          {admin && (
            <Link to={admin.path} className={`${LINK} border border-white/25 text-white hover:bg-white/10`}>
              Admin console
            </Link>
          )}
          <div className="relative" ref={wrapRef}>
            <button
              ref={buttonRef}
              type="button"
              aria-haspopup="menu"
              aria-expanded={open}
              aria-controls={menuId}
              onClick={() => setOpen((o) => !o)}
              className={`${LINK} flex max-w-[16rem] items-center gap-1 text-white hover:bg-white/10`}
              title={user?.email}
            >
              <span className="truncate">{name}</span>
              <span aria-hidden>▾</span>
            </button>
            {open && (
              <ul
                id={menuId}
                role="menu"
                aria-label="Account"
                className="absolute right-0 z-50 mt-1 w-56 overflow-hidden rounded-md border border-black/10 bg-white py-1 text-[13px] text-[#10252d] shadow-lg"
              >
                <li role="none" className="px-3 pb-1 pt-1.5 text-[11px] text-[#60747b]">{user?.email}</li>
                <li role="none">
                  <Link role="menuitem" to="/portal/settings" onClick={() => setOpen(false)} className="block px-3 py-2 hover:bg-[#f0f5f4] focus:bg-[#f0f5f4] focus:outline-none">
                    Profile &amp; settings
                  </Link>
                </li>
                <li role="none">
                  <Link role="menuitem" to="/account/security" onClick={() => setOpen(false)} className="block px-3 py-2 hover:bg-[#f0f5f4] focus:bg-[#f0f5f4] focus:outline-none">
                    Sign-in security
                  </Link>
                </li>
                {canPrefer && (
                  <li role="none">
                    <button role="menuitem" type="button" onClick={makeDefault} className="block w-full px-3 py-2 text-left hover:bg-[#f0f5f4] focus:bg-[#f0f5f4] focus:outline-none">
                      Land here when I sign in
                    </button>
                  </li>
                )}
                <li role="none" className="my-1 border-t border-black/10" />
                <li role="none">
                  <button role="menuitem" type="button" onClick={signOut} className="block w-full px-3 py-2 text-left font-medium hover:bg-[#f0f5f4] focus:bg-[#f0f5f4] focus:outline-none">
                    Sign out
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
